import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { StageScreen } from '../components/features/cuttysark/stage/StageScreen.js';
import { useCuttysarkLive } from '../hooks/useCuttysark.js';
import { ApiError } from '../lib/api/_request.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞のステージ画面。運営が Meet で画面共有する全画面の演出（代表者紹介アニメーション・発表中・投票受付・集計中・結果発表のドラムロールと紙吹雪・応援コメント・表彰台）
 * @graph-connects useCuttysarkLive [calls] 進行状態のポーリング
 * @graph-connects StageScreen [calls] ステージ画面の本体
 */
export const Route = createFileRoute('/cuttysark/$sessionId/stage')({
  component: CuttysarkStageRoute,
});

/**
 * ページ単位のガードは掛けない（参加者画面と同じく、見られるかどうかは API に任せ、403 は
 * API のメッセージをそのまま出す）。
 * @graph-connects CuttysarkStagePage [calls] ページ本体
 */
function CuttysarkStageRoute(): ReactNode {
  return <CuttysarkStagePage />;
}

/** @graph-connects none */
function CuttysarkStagePage(): ReactNode {
  const { t } = useTranslation();
  const { sessionId } = Route.useParams();
  const { data: view, error, isLoading } = useCuttysarkLive(sessionId);

  if (isLoading) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.common.loading')}</p>
    );
  }
  if (view === undefined) {
    return (
      <p className="text-sm text-[var(--color-error)]">
        {error instanceof ApiError && error.status === 404
          ? t('cuttysark.common.notFound')
          : error instanceof ApiError && error.status === 403
            ? error.message
            : t('cuttysark.common.loadError')}
      </p>
    );
  }
  return <StageScreen sessionId={sessionId} view={view} />;
}
